import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ImageUpload } from "@/components/shared";
import { UploadFolder } from "@/services/filesService";
import { resolveUploadUrl } from "@/services/filesService";
import { useToast } from "@/hooks/use-toast";
import { useMyStore, useStoreId, useUpdateStoreKycMutation } from "@/hooks/queries";
import { getApiErrorMessage } from "@/services/httpClient";
import { FileText, User, CheckCircle2, AlertTriangle, Building, ShieldAlert } from "lucide-react";
import type { StoreResponseDto, StoreKycDto } from "@/types/store.dto";

function storeToKycForm(store: StoreResponseDto): StoreKycDto {
  return {
    gstNumber: store.gstNumber ?? "",
    gstCertificateUrl: store.gstCertificateUrl ?? "",
    panNumber: store.panNumber ?? "",
    selfieUrl: store.selfieUrl ?? "",
    shopCertificateUrl: store.shopCertificateUrl ?? "",
    aadharNumber: store.aadharNumber ?? "",
    aadharCardUrl: store.aadharCardUrl ?? "",
    bankAccountName: store.bankAccountName ?? "",
    bankAccountNumber: store.bankAccountNumber ?? "",
    bankIfscCode: store.bankIfscCode ?? "",
    bankName: store.bankName ?? "",
  };
}

const StoreKycPage = () => {
  const { toast } = useToast();
  const { storeId } = useStoreId();
  const { data: store, isLoading, refetch } = useMyStore(Boolean(storeId));
  const updateKyc = useUpdateStoreKycMutation(storeId ?? 0);
  const [form, setForm] = useState<StoreKycDto | null>(null);

  useEffect(() => {
    if (store) {
      setForm(storeToKycForm(store));
    }
  }, [store]);

  const handleSave = async () => {
    if (!store || !form || !storeId) return;

    try {
      await updateKyc.mutateAsync(form);
      toast({ title: "KYC details updated successfully" });
      refetch();
    } catch (error) {
      toast({
        title: "Could not update KYC",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const updateField = <K extends keyof StoreKycDto>(key: K, value: StoreKycDto[K]) => {
    setForm((current) => (current ? { ...current, [key]: value } : current));
  };

  if (isLoading || !form || !store) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-muted rounded" />
        <div className="h-32 bg-muted rounded-xl" />
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="h-96 bg-muted rounded-xl" />
          <div className="h-96 bg-muted rounded-xl" />
        </div>
      </div>
    );
  }

  const isApproved = store.statusText === "Active" || store.statusText === "approved" || store.kycVerified;
  const isRejected = store.statusText === "Rejected";

  return (
    <div className="space-y-6 max-w-5xl mx-auto px-4 py-6">
      <div>
        <h1 className="text-2xl font-heading font-bold tracking-tight lg:text-3xl flex items-center gap-2">
          <FileText className="h-7 w-7 text-primary" /> Store KYC Verification
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Provide your business identity documents and bank account details for settlement.
        </p>
      </div>

      {/* KYC Status Indicator Banner */}
      <Card className={`overflow-hidden border-l-4 ${
        isApproved 
          ? "border-l-success bg-success/5" 
          : isRejected 
            ? "border-l-destructive bg-destructive/5" 
            : "border-l-warning bg-warning/5"
      }`}>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              {isApproved ? (
                <CheckCircle2 className="h-6 w-6 text-success shrink-0 mt-0.5" />
              ) : isRejected ? (
                <ShieldAlert className="h-6 w-6 text-destructive shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle className="h-6 w-6 text-warning shrink-0 mt-0.5" />
              )}
              <div>
                <h3 className="font-bold text-base flex items-center gap-2">
                  KYC Status: 
                  <Badge
                    variant={isRejected ? "destructive" : "outline"}
                    className={
                      isApproved
                        ? "bg-success/10 text-success border-success/20"
                        : isRejected
                          ? ""
                          : "bg-warning/10 text-warning border-warning/20"
                    }
                  >
                    {isApproved ? "Verified & Active" : isRejected ? "Rejected" : "Pending Review"}
                  </Badge>
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {isApproved 
                    ? "Your store KYC is fully verified. You have active selling and settlement permissions." 
                    : isRejected 
                      ? `Your application was rejected. Reason: ${store.rejectionReason || "Incomplete documents"}. Please upload correct files and re-submit.`
                      : "Your documents are currently pending review by our compliance team. Reviews take up to 24 hours."
                  }
                </p>
              </div>
            </div>
            {isApproved && (
              <Badge className="bg-success text-success-foreground self-start sm:self-auto border-0">
                Verified Store
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Document uploads card */}
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="h-5 w-5 text-primary" /> Identity & Verification Documents
            </CardTitle>
            <CardDescription>Upload image files of your documents. Make sure details are clearly legible.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Selfie upload */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Owner Selfie Photograph</Label>
              <ImageUpload
                label="Selfie Photo"
                value={form.selfieUrl}
                onChange={(url) => updateField("selfieUrl", url)}
                folder={UploadFolder.Stores}
                hint="Your face must be clearly visible"
                layout="stacked"
              />
            </div>

            <Separator />

            {/* Aadhaar number & image */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="aadharNumber" className="text-sm font-semibold">Aadhaar Card Number</Label>
                <Input
                  id="aadharNumber"
                  placeholder="12-digit Aadhaar number"
                  maxLength={12}
                  value={form.aadharNumber}
                  onChange={(e) => updateField("aadharNumber", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Aadhaar Card Front Copy</Label>
                <ImageUpload
                  label="Aadhaar Photo"
                  value={form.aadharCardUrl}
                  onChange={(url) => updateField("aadharCardUrl", url)}
                  folder={UploadFolder.Stores}
                  hint="Clear copy of your Aadhaar card front"
                  layout="stacked"
                />
              </div>
            </div>

            <Separator />

            {/* Shop Registration Certificate */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Shop Registration Certificate</Label>
              <ImageUpload
                label="Shop Certificate"
                value={form.shopCertificateUrl}
                onChange={(url) => updateField("shopCertificateUrl", url)}
                folder={UploadFolder.Stores}
                hint="Trade license or shop establishment document"
                layout="stacked"
              />
            </div>
          </CardContent>
        </Card>

        {/* Business & Bank Info card */}
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building className="h-5 w-5 text-primary" /> Tax Numbers & Settlements
            </CardTitle>
            <CardDescription>Enter tax registration details and bank details for automated payouts.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* GST details */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="gstNumber" className="text-sm font-semibold">GSTIN Number</Label>
                <Input
                  id="gstNumber"
                  placeholder="15-digit GSTIN (Optional)"
                  className="uppercase"
                  value={form.gstNumber}
                  onChange={(e) => updateField("gstNumber", e.target.value.toUpperCase())}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">GST Registration Certificate</Label>
                <ImageUpload
                  label="GST Certificate"
                  value={form.gstCertificateUrl}
                  onChange={(url) => updateField("gstCertificateUrl", url)}
                  folder={UploadFolder.Stores}
                  hint="Official GSTIN certificate copy"
                  layout="stacked"
                />
              </div>
            </div>

            <Separator />

            {/* PAN card details */}
            <div className="space-y-2">
              <Label htmlFor="panNumber" className="text-sm font-semibold">PAN Card Number</Label>
              <Input
                id="panNumber"
                placeholder="10-digit PAN (Optional)"
                className="uppercase"
                maxLength={10}
                value={form.panNumber}
                onChange={(e) => updateField("panNumber", e.target.value.toUpperCase())}
              />
            </div>

            <Separator />

            {/* Bank details */}
            <div className="space-y-4 pt-1">
              <h4 className="text-sm font-bold text-muted-foreground flex items-center gap-1.5">
                Bank Payout Details
              </h4>
              <div className="space-y-2">
                <Label htmlFor="bankName" className="text-sm font-semibold">Bank Name</Label>
                <Input
                  id="bankName"
                  placeholder="e.g. State Bank of India"
                  value={form.bankName}
                  onChange={(e) => updateField("bankName", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bankAccountName" className="text-sm font-semibold">Account Holder Name</Label>
                <Input
                  id="bankAccountName"
                  placeholder="Official name matching bank records"
                  value={form.bankAccountName}
                  onChange={(e) => updateField("bankAccountName", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bankAccountNumber" className="text-sm font-semibold">Bank Account Number</Label>
                <Input
                  id="bankAccountNumber"
                  placeholder="Account number for settlement"
                  value={form.bankAccountNumber}
                  onChange={(e) => updateField("bankAccountNumber", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bankIfscCode" className="text-sm font-semibold">Bank IFSC Code</Label>
                <Input
                  id="bankIfscCode"
                  placeholder="11-character bank code"
                  maxLength={11}
                  className="uppercase"
                  value={form.bankIfscCode}
                  onChange={(e) => updateField("bankIfscCode", e.target.value.toUpperCase())}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col gap-3 rounded-xl border bg-muted/40 p-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-md text-xs text-muted-foreground">
          By saving details, you certify that all uploaded documents and numbers are genuine and belong to the registered store owner.
        </p>
        <Button
          onClick={handleSave}
          disabled={updateKyc.isPending}
          className="w-full shrink-0 gap-2 px-6 sm:w-auto"
        >
          Save & Submit KYC
        </Button>
      </div>
    </div>
  );
};

export default StoreKycPage;
